import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageSquare, CreditCard, Users, HelpCircle, Quote,
  FileText, Tag, Megaphone, LogOut, Plus, Trash2, Save, Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { useAdmin } from "../../lib/admin";
import { api } from "../../lib/api";
import Seo from "../../components/Seo";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "contacts", label: "Contacts", icon: MessageSquare },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "subscriptions", label: "Subscriptions", icon: Users },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "legal", label: "Legal Docs", icon: FileText },
  { id: "plans", label: "Pricing", icon: Tag },
  { id: "promos", label: "Promos & Banners", icon: Megaphone },
  { id: "webhooks", label: "Webhook Logs", icon: Webhook },
];

const LEGAL_SLUGS = ["privacy", "terms", "refund", "subscription", "data-usage"];

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Table({ cols, rows, render }) {
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5">
          <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3 font-semibold text-zinc-400">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-zinc-600">No records</td></tr>
          ) : rows.map(render)}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});

  const load = async (key, url) => {
    try { const r = await api.get(url); setData((d) => ({ ...d, [key]: r.data })); } catch {}
  };

  useEffect(() => {
    load("contacts", "/admin/contacts");
    load("payments", "/admin/payments");
    load("subscriptions", "/admin/subscriptions");
    load("faqs", "/content/faqs");
    load("testimonials", "/content/testimonials");
    load("plans", "/plans");
    load("promos", "/admin/promo-banners");
    load("webhooks", "/admin/webhook-logs");
  }, []);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#070708] flex">
      <Seo title="Admin Dashboard — ASCENDXP" />
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#0a0a0c] flex flex-col fixed h-full">
        <div className="p-5 border-b border-white/10">
          <span className="font-heading font-black text-lg">ASCEND<span className="text-gradient-indigo">XP</span></span>
          <p className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">Admin Console</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === n.id ? "bg-indigo-600/20 text-white" : "text-zinc-400 hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="text-xs text-zinc-500 px-3 mb-2 truncate">{admin?.email}</div>
          <button data-testid="admin-logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:bg-white/5">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-8">
        {tab === "overview" && (
          <Section title="Overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Contacts", data.contacts?.length || 0],
                ["Payments", data.payments?.length || 0],
                ["Active Subs", (data.subscriptions || []).filter((s) => s.premiumStatus === "active").length],
                ["Webhook Events", data.webhooks?.length || 0],
              ].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="text-3xl font-heading font-black text-gradient-indigo">{v}</div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "contacts" && (
          <Section title="Contact Requests">
            <Table cols={["Name", "Email", "Subject", "Message", "Date"]} rows={data.contacts || []}
              render={(c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 text-zinc-400">{c.email}</td>
                  <td className="px-4 py-3">{c.subject}</td>
                  <td className="px-4 py-3 text-zinc-400 max-w-xs truncate">{c.message}</td>
                  <td className="px-4 py-3 text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              )} />
          </Section>
        )}

        {tab === "payments" && (
          <Section title="Payments">
            <Table cols={["User", "Plan", "Cycle", "Amount", "Status", "Date"]} rows={data.payments || []}
              render={(p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-xs">{p.userId}</td>
                  <td className="px-4 py-3 capitalize">{p.subscriptionPlan}</td>
                  <td className="px-4 py-3 capitalize text-zinc-400">{p.billingCycle}</td>
                  <td className="px-4 py-3">₹{(p.amount / 100).toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusPill status={p.paymentStatus} /></td>
                  <td className="px-4 py-3 text-zinc-600">{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              )} />
          </Section>
        )}

        {tab === "subscriptions" && (
          <Section title="Subscriptions">
            <Table cols={["User", "Plan", "Cycle", "Status", "Expires"]} rows={data.subscriptions || []}
              render={(s) => (
                <tr key={s.userId} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-xs">{s.userId}</td>
                  <td className="px-4 py-3 capitalize">{s.subscriptionPlan}</td>
                  <td className="px-4 py-3 capitalize text-zinc-400">{s.billingCycle}</td>
                  <td className="px-4 py-3"><StatusPill status={s.premiumStatus} /></td>
                  <td className="px-4 py-3 text-zinc-600">{s.expiryDate ? new Date(s.expiryDate).toLocaleDateString() : "—"}</td>
                </tr>
              )} />
          </Section>
        )}

        {tab === "faqs" && <FaqManager faqs={data.faqs || []} reload={() => load("faqs", "/content/faqs")} />}
        {tab === "testimonials" && <TestimonialManager items={data.testimonials || []} reload={() => load("testimonials", "/content/testimonials")} />}
        {tab === "legal" && <LegalManager />}
        {tab === "plans" && <PlanManager plans={data.plans || []} reload={() => load("plans", "/plans")} />}
        {tab === "promos" && <PromoManager promos={data.promos || []} reload={() => load("promos", "/admin/promo-banners")} />}

        {tab === "webhooks" && (
          <Section title="Webhook Logs">
            <Table cols={["Event", "Event ID", "Received"]} rows={data.webhooks || []}
              render={(w) => (
                <tr key={w.id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-xs">{w.event}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{w.event_id}</td>
                  <td className="px-4 py-3 text-zinc-600">{new Date(w.received_at).toLocaleString()}</td>
                </tr>
              )} />
          </Section>
        )}
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-500/15 text-emerald-400", active: "bg-emerald-500/15 text-emerald-400",
    created: "bg-amber-500/15 text-amber-400", failed: "bg-red-500/15 text-red-400",
    refunded: "bg-zinc-500/15 text-zinc-400", cancelled: "bg-red-500/15 text-red-400",
    inactive: "bg-zinc-500/15 text-zinc-400",
  };
  return <span className={`text-xs px-2 py-1 rounded-full capitalize ${map[status] || "bg-zinc-500/15 text-zinc-400"}`}>{status}</span>;
}

function FaqManager({ faqs, reload }) {
  const [form, setForm] = useState({ question: "", answer: "", category: "General", order: 0 });
  const add = async () => {
    if (!form.question || !form.answer) return toast.error("Fill question & answer");
    await api.post("/admin/faqs", form);
    toast.success("FAQ added"); setForm({ question: "", answer: "", category: "General", order: 0 }); reload();
  };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="FAQs">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 mb-6 grid gap-3">
        <input data-testid="faq-question-input" placeholder="Question" className="input-dark" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
        <textarea data-testid="faq-answer-input" placeholder="Answer" rows={2} className="input-dark resize-none" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
        <div className="flex gap-3">
          <input placeholder="Category" className="input-dark" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <button data-testid="faq-add-btn" onClick={add} className="btn-primary shrink-0"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>
      <div className="space-y-2">
        {faqs.map((f) => (
          <div key={f.id} className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
            <div><div className="font-medium text-sm">{f.question}</div><div className="text-xs text-zinc-500">{f.category}</div></div>
            <button data-testid={`faq-delete-${f.id}`} onClick={() => del(f.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function TestimonialManager({ items, reload }) {
  const [form, setForm] = useState({ name: "", role: "", quote: "", rank: "Elite", order: 0 });
  const add = async () => {
    if (!form.name || !form.quote) return toast.error("Fill name & quote");
    await api.post("/admin/testimonials", form);
    toast.success("Added"); setForm({ name: "", role: "", quote: "", rank: "Elite", order: 0 }); reload();
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="Testimonials">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 mb-6 grid gap-3">
        <div className="grid grid-cols-3 gap-3">
          <input placeholder="Name" className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Role" className="input-dark" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          <input placeholder="Rank" className="input-dark" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />
        </div>
        <textarea placeholder="Quote" rows={2} className="input-dark resize-none" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} />
        <button data-testid="testimonial-add-btn" onClick={add} className="btn-primary w-fit"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
            <div><div className="font-medium text-sm">{t.name} · <span className="text-zinc-500">{t.role}</span></div><div className="text-xs text-zinc-500 max-w-xl truncate">{t.quote}</div></div>
            <button onClick={() => del(t.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function LegalManager() {
  const [slug, setSlug] = useState("privacy");
  const [doc, setDoc] = useState({ title: "", content: "" });
  useEffect(() => {
    api.get(`/content/legal/${slug}`).then((r) => setDoc({ title: r.data.title, content: r.data.content })).catch(() => setDoc({ title: "", content: "" }));
  }, [slug]);
  const save = async () => {
    await api.put(`/admin/legal/${slug}`, { slug, title: doc.title, content: doc.content });
    toast.success("Saved new version");
  };
  return (
    <Section title="Legal Documents">
      <div className="flex gap-2 mb-5 flex-wrap">
        {LEGAL_SLUGS.map((s) => (
          <button key={s} onClick={() => setSlug(s)} className={`px-3 py-1.5 rounded-full text-sm capitalize ${slug === s ? "bg-indigo-600 text-white" : "bg-white/5 text-zinc-400"}`}>{s.replace("-", " ")}</button>
        ))}
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 grid gap-3">
        <input className="input-dark" value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} placeholder="Title" />
        <textarea data-testid="legal-content-input" rows={16} className="input-dark resize-none font-mono text-xs" value={doc.content} onChange={(e) => setDoc({ ...doc, content: e.target.value })} placeholder="Content (use ## for headings)" />
        <button data-testid="legal-save-btn" onClick={save} className="btn-primary w-fit"><Save className="w-4 h-4" /> Save Version</button>
      </div>
    </Section>
  );
}

function PlanManager({ plans, reload }) {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    await api.put(`/admin/plans/${edit.id}`, { ...edit, features: typeof edit.features === "string" ? edit.features.split("\n").filter(Boolean) : edit.features });
    toast.success("Plan saved"); setEdit(null); reload();
  };
  return (
    <Section title="Pricing Plans">
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="font-heading font-bold">{p.name}</div>
            <div className="text-sm text-zinc-400 mt-1">₹{p.monthly_price}/mo · ₹{p.yearly_price}/yr</div>
            <button onClick={() => setEdit({ ...p, features: p.features.join("\n") })} className="btn-ghost mt-4 text-sm py-2">Edit</button>
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-6 rounded-xl border border-indigo-500/30 bg-white/[0.02] p-5 grid gap-3">
          <h3 className="font-bold">Editing: {edit.name}</h3>
          <div className="grid grid-cols-2 gap-3">
            <input className="input-dark" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" />
            <input className="input-dark" value={edit.tagline} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} placeholder="Tagline" />
            <input type="number" className="input-dark" value={edit.monthly_price} onChange={(e) => setEdit({ ...edit, monthly_price: Number(e.target.value) })} placeholder="Monthly ₹" />
            <input type="number" className="input-dark" value={edit.yearly_price} onChange={(e) => setEdit({ ...edit, yearly_price: Number(e.target.value) })} placeholder="Yearly ₹" />
          </div>
          <textarea rows={6} className="input-dark resize-none" value={edit.features} onChange={(e) => setEdit({ ...edit, features: e.target.value })} placeholder="One feature per line" />
          <div className="flex gap-2">
            <button data-testid="plan-save-btn" onClick={save} className="btn-primary"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => setEdit(null)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function PromoManager({ promos, reload }) {
  const [form, setForm] = useState({ title: "", subtitle: "", cta_label: "", cta_url: "", active: true });
  const add = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/admin/promo-banners", form);
    toast.success("Banner created"); setForm({ title: "", subtitle: "", cta_label: "", cta_url: "", active: true }); reload();
  };
  const del = async (id) => { await api.delete(`/admin/promo-banners/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="Promos & Banners">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 mb-6 grid gap-3">
        <input data-testid="promo-title-input" placeholder="Banner title (e.g. Launch week: 40% off Elite)" className="input-dark" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="CTA label" className="input-dark" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
          <input placeholder="CTA url (e.g. /pricing)" className="input-dark" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Set active (shows site-wide)
        </label>
        <button data-testid="promo-add-btn" onClick={add} className="btn-primary w-fit"><Plus className="w-4 h-4" /> Create Banner</button>
      </div>
      <div className="space-y-2">
        {promos.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-0.5 rounded-full ${b.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-500"}`}>{b.active ? "Active" : "Off"}</span>
              <span className="text-sm">{b.title}</span>
            </div>
            <button onClick={() => del(b.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}
